import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface Notification {
  id: string;
  user_id: string;
  type: 'new_subscriber' | 'subscription_cancelled' | 'calendar_message' | 'event_feedback' | 'calendar_update';
  data: any;
  read: boolean;
  created_at: string;
}

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  subscribeToNotifications: (userId: string) => void;
  unsubscribeFromNotifications: () => void;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  subscription: null,

  fetchNotifications: async () => {
    set({ loading: true });
    try {
      const { data: notifications, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const unreadCount = notifications?.filter(n => !n.read).length || 0;

      set({ 
        notifications: notifications || [],
        unreadCount,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching notifications:', error);
      set({ loading: false });
    }
  },

  markAsRead: async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id);

      if (error) throw error;

      const { notifications } = get();
      const updatedNotifications = notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      );

      set({ 
        notifications: updatedNotifications,
        unreadCount: get().unreadCount - 1
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('read', false);

      if (error) throw error;

      const { notifications } = get();
      const updatedNotifications = notifications.map(n => ({ ...n, read: true }));

      set({ 
        notifications: updatedNotifications,
        unreadCount: 0
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  },

  subscribeToNotifications: (userId: string) => {
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          const { notifications, unreadCount } = get();
          
          set({
            notifications: [newNotification, ...notifications],
            unreadCount: unreadCount + 1
          });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  },

  unsubscribeFromNotifications: () => {
    supabase.removeAllChannels();
  }
}));